import { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams, useRouter } from '@/routing/navigation'
import { clearReferralCookies } from '@/lib/utils/referral-cookie'
import Link from '@/routing/Link'
import { ArrowLeft, Loader2, Mail, RotateCw } from 'lucide-react'
import { authApi } from '@/lib/api'
import { useAuth } from '@/lib/config/auth-provider'
import { safeReturnTo } from '@/lib/utils/safe-return-to'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Heading } from '@/components/ui/Heading'
import { Text } from '@/components/ui/Text'

const RESEND_COOLDOWN_SECONDS = 60

export default function VerifyEmailPage() {
  const { t } = useTranslation(['auth', 'common'])
  const searchParams = useSearchParams()
  const router = useRouter()
  const { loginWithResponse } = useAuth()
  const [code, setCode] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [resendCooldown, setResendCooldown] = useState(RESEND_COOLDOWN_SECONDS)
  const [resendMessage, setResendMessage] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  const email = searchParams.get('email') || ''

  useEffect(() => {
    if (!email) {
      router.replace('/register')
    }
  }, [email, router])

  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  useEffect(() => {
    if (resendCooldown <= 0) return
    const timer = setInterval(() => {
      setResendCooldown((prev) => prev - 1)
    }, 1000)
    return () => clearInterval(timer)
  }, [resendCooldown])

  if (!email) return null

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (code.length !== 6) return
    setLoading(true)
    setError('')

    try {
      const response = await authApi.verifyEmail(email, code)
      clearReferralCookies()
      loginWithResponse({ ...response, isNewUser: true })
      const returnTo = searchParams.get('returnTo')
      router.push(safeReturnTo(returnTo))
    } catch (err: any) {
      setError(err.response?.data?.message || t('verify.errors.invalidCode'))
    } finally {
      setLoading(false)
    }
  }

  const handleResend = async () => {
    if (resendCooldown > 0) return
    setResendMessage('')
    setError('')

    try {
      await authApi.resendVerification(email)
      setResendMessage(t('verify.resent'))
      setResendCooldown(RESEND_COOLDOWN_SECONDS)
    } catch {
      setError(t('verify.errors.resendFailed'))
    }
  }

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6)
    setCode(value)
  }

  return (
    <div className="flex w-full flex-col gap-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div className="flex flex-col gap-1">
        <Heading level={1} size="3xl" className="text-center font-display font-normal tracking-tight">
          {t('verify.title')}
        </Heading>
        <Text as="p" size="sm" color="muted" className="text-center">
          {t('verify.sentTo')}
        </Text>
        <Text as="p" size="sm" weight="medium" className="text-center">
          {email}
        </Text>
      </div>

      <div className="rounded-lg border border-primary/20 bg-primary/5 p-4 text-sm">
        <div className="flex items-start gap-3">
          <Mail className="mt-0.5 size-5 shrink-0 text-primary" />
          <div>
            <p className="font-medium text-foreground">{t('verify.inboxTitle')}</p>
            <Text as="p" size="sm" color="muted" className="mt-1">
              {t('verify.inboxBody')}
            </Text>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <Input
          ref={inputRef}
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          value={code}
          onChange={handleCodeChange}
          placeholder="000000"
          maxLength={6}
          className="text-center text-2xl font-mono tracking-[0.5em] h-14"
        />

        {error && (
          <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
            {error}
          </div>
        )}

        {resendMessage && (
          <div className="rounded-lg border border-primary/20 bg-primary/5 p-3 text-sm text-foreground">
            {resendMessage}
          </div>
        )}

        <Button type="submit" variant="cta" disabled={loading || code.length !== 6} className="w-full gap-2">
          {loading ? (
            <>
              <Loader2 className="mr-2 size-4 animate-spin" />
              {t('verify.submitting')}
            </>
          ) : (
            t('verify.submit')
          )}
        </Button>
      </form>

      <div className="flex items-center justify-between">
        <Text as="p" size="sm" color="muted">
          <Button variant="link" size="sm" className="h-auto gap-1 p-0 text-sm" asChild>
            <Link href="/register">
              <ArrowLeft className="size-3" />
              {t('verify.startOver')}
            </Link>
          </Button>
        </Text>

        <Button
          variant="link"
          size="sm"
          className="h-auto gap-1 p-0 text-sm"
          disabled={resendCooldown > 0}
          onClick={handleResend}
        >
          <RotateCw className="size-3" />
          {resendCooldown > 0 ? t('verify.resendIn', { seconds: resendCooldown }) : t('verify.resend')}
        </Button>
      </div>
    </div>
  )
}
